import { useCallback, useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'ep_intro_seen';

/**
 * [모듈 5] 인트로 영상 팝업.
 * - 페이지 로드 시 세션당 1회 자동으로 열림 (sessionStorage 'ep_intro_seen')
 * - 닫기: X 버튼 / 배경 클릭 / ESC → 영상 일시정지
 * - 영상이 끝까지 재생되면 자동으로 닫힘
 * - 닫은 뒤에는 좌하단 플로팅 버튼(▶ 인트로 영상)으로 다시 열 수 있음
 * - 자동재생은 브라우저 정책상 muted로 시작. "소리 켜고 보기" 버튼으로 음소거 해제 가능
 *   (해제 시 버튼 자동 숨김 — video의 volumechange 이벤트로 감지)
 */
export default function useIntroModal() {
  const videoRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showFab, setShowFab] = useState(false);

  const openModal = useCallback(() => {
    setIsOpen(true);
    setShowFab(false);
    document.body.style.overflow = 'hidden'; // 배경 스크롤 잠금
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      const p = video.play(); // 무음이라 자동재생 허용
      if (p && p.catch) p.catch(() => {}); // 자동재생 차단 시 조용히 무시
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setShowFab(true);
    document.body.style.overflow = '';
    const video = videoRef.current;
    if (video) video.pause();
  }, []);

  // 소리 켜기: 누르면 음소거 해제 (controls로 직접 켜도 동일하게 버튼이 사라짐)
  const handleUnmute = useCallback(() => {
    const video = videoRef.current;
    if (video) video.muted = false;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onVolumeChange = () => setIsMuted(video.muted);
    const onEnded = () => closeModal(); // 영상 끝나면 자동 닫기
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('ended', onEnded);
    };
  }, [closeModal]);

  useEffect(() => {
    const onKeydown = (e) => {
      if (e.key === 'Escape' && isOpen) closeModal();
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [isOpen, closeModal]);

  // 세션당 1회만 자동 오픈 (새로고침 반복 시 짜증 방지)
  // 주의: sessionStorage.setItem은 타이머 콜백 안에서(실제로 열리는 시점에) 찍는다.
  // 여기서 바로 찍으면 React 18 StrictMode(dev 모드)가 이 effect를 mount 직후
  // 한 번 더 재실행할 때 "이미 봤음" 상태로 착각해서 자동 오픈이 아예 스킵된다.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) {
      setShowFab(true);
      return undefined;
    }
    const t = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, '1');
      openModal();
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { videoRef, isOpen, isMuted, showFab, openModal, closeModal, handleUnmute };
}
